import {ReactElement, useEffect} from "react";
import {useBulkAddDriversMutation, useLoadSingleDriverCustomItemNoteLazyQuery, VersionType} from "../../../__generated__/generated_types";
import {CopyDriverModal, CopyDriverModalOnSubmitArgs} from "./CopyDriverModal";


export interface CopyCustomDriverSourceDriver {
    driverId: string;
    name: string;
    propertyId: string;
    propertyName: string;
    destinationAccountId: string;
    accountLabel: string;
    status: string;
    locked: boolean;
    valuesLocked: boolean;
    amounts: (number | null)[];
    percents: (number | null)[];
    counts: (number | null)[];
}

export interface CopyCustomDriverAssumptionsModalProps {
    visible: boolean;
    budgetYear: number;
    versionType: VersionType.Budget | VersionType.Reforecast;
    sourceDriver: CopyCustomDriverSourceDriver;
    destinationProperties: {id: string; name: string, disabled?: boolean}[];
    defaultSelectedProperties?: string[];
    accounts: {id: string; label: string}[];
    onClose: () => void;
    onCopied: () => void;
}

export function CopyCustomDriverAssumptionsModal(props: CopyCustomDriverAssumptionsModalProps): ReactElement {
    const {
        visible,
        budgetYear,
        versionType,
        sourceDriver,
        destinationProperties,
        defaultSelectedProperties,
        accounts,
        onClose,
        onCopied
    } = props;

    const [bulkAddDrivers, {loading: copyLoading}] = useBulkAddDriversMutation({notifyOnNetworkStatusChange: true});
    const [fetchNote, {data: noteData, loading: noteLoading}] = useLoadSingleDriverCustomItemNoteLazyQuery({fetchPolicy: "no-cache"});

    useEffect(() => {
        fetchNote({
            variables: {
                driverCustomItemId: sourceDriver.driverId,
            }
        });
    }, [sourceDriver.driverId]);

    const handleCopy = async (args: CopyDriverModalOnSubmitArgs) => {
        const {
            copyNote,
            copyStatus,
            selectedPropertyIds,
            selectedAccountIds,
        } = args;

        if (noteLoading) {
            return;
        }

        let note = null;
        let noteReadOnly = false;
        if (copyNote === true && typeof noteData?.loadSingleDriverCustomItemNote?.note === "string" && noteData?.loadSingleDriverCustomItemNote?.note.trim() !== "") {
            note = noteData?.loadSingleDriverCustomItemNote?.note;
            noteReadOnly = noteData?.loadSingleDriverCustomItemNote?.isReadOnly;
        }

        const transformedAmounts = sourceDriver.amounts.map(a => a == null ? "0" : String(a));
        const transformedPercents = sourceDriver.percents.map(p => p == null ? "0" : String(p));
        const transformedCounts = sourceDriver.counts.map(c => c == null ? "0" : String(c));
        const result = await bulkAddDrivers({
            variables: {
                propertyIds: selectedPropertyIds,
                accountIds: selectedAccountIds,
                year: versionType === VersionType.Reforecast ? budgetYear - 1 : budgetYear,
                versionType: versionType,
                overwriteOnDuplicate: true,
                custom: {
                    itemName: sourceDriver.name,
                    monthlyPercents: transformedPercents,
                    monthlyAmounts: transformedAmounts,
                    monthlyCount: transformedCounts,
                    locked: sourceDriver.locked,
                    valuesLocked: sourceDriver.valuesLocked,
                    note: note,
                    noteReadOnly: noteReadOnly,
                },
                copyNote: copyNote,
                copyStatus: copyStatus,
            }
        });
        return result.data?.bulkAddDrivers;
    };

    return (
        <CopyDriverModal
            visible={visible}
            sourceDriver={sourceDriver}
            destinationProperties={destinationProperties}
            defaultSelectedProperties={defaultSelectedProperties}
            accounts={accounts}
            note={noteData?.loadSingleDriverCustomItemNote?.note}
            noteLoading={noteLoading}
            copyBusy={copyLoading}
            onSubmit={handleCopy}
            onClose={onClose}
            onCopied={onCopied}
        />
    );
}
